import React, { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Persistent per-folder manifest so an Aw-Snap tab crash mid-upload doesn't
// force the photographer to start again. Stored under a stable key derived
// from the root folder + total file count so re-picking the same folder is
// recognised across reloads.
const MANIFEST_KEY = (rootFolder, total) => `pm.folderManifest:${rootFolder}:${total}`;

function loadManifest(rootFolder, total) {
  try {
    const raw = localStorage.getItem(MANIFEST_KEY(rootFolder, total));
    const parsed = raw ? JSON.parse(raw) : {};
    return { uploaded: parsed.uploaded || [], failed: parsed.failed || [] };
  } catch { return { uploaded: [], failed: [] }; }
}

function saveManifest(rootFolder, total, uploaded, failed = []) {
  try {
    localStorage.setItem(
      MANIFEST_KEY(rootFolder, total),
      JSON.stringify({ uploaded, failed, savedAt: Date.now() }),
    );
  } catch { /* localStorage full — ignore */ }
}

function clearManifest(rootFolder, total) {
  try { localStorage.removeItem(MANIFEST_KEY(rootFolder, total)); } catch (_e) { /* localStorage unavailable */ }
}

// ---- Tree building --------------------------------------------------------
// The browser gives every File a `webkitRelativePath` like
// "100mdsc/mx1/race1/DSC_001.jpg". We split that into folders and files so we
// can show the user the shape of their upload and ask where each leaf goes.

const IMAGE_EXT = /\.(jpe?g|png|webp|gif|heic)$/i;

// Recursively walk a DataTransferItem entry (dropped folder) and return
// all image files, tagged with their relative path.
async function readEntry(entry, pathPrefix = "") {
  const results = [];
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    if (IMAGE_EXT.test(file.name)) {
      // Synthesize webkitRelativePath so the tree builder works
      const relPath = pathPrefix ? `${pathPrefix}/${file.name}` : file.name;
      Object.defineProperty(file, "webkitRelativePath", { value: relPath, configurable: true });
      results.push(file);
    }
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    const nextPrefix = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;
    // readEntries only returns a batch at a time; loop until empty
    while (true) {
      const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) break;
      for (const child of batch) {
        const kids = await readEntry(child, nextPrefix);
        results.push(...kids);
      }
    }
  }
  return results;
}

async function filesFromDrop(dataTransfer) {
  const items = Array.from(dataTransfer.items || []);
  const entries = items.map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean);
  if (entries.length === 0) {
    // Fallback: plain files (no folder support)
    return Array.from(dataTransfer.files || []).filter((f) => IMAGE_EXT.test(f.name));
  }
  const all = [];
  for (const entry of entries) {
    const files = await readEntry(entry);
    all.push(...files);
  }
  return all;
}

function buildTree(files) {
  const root = { name: "", path: "", children: {}, files: [] };
  for (const f of files) {
    if (!IMAGE_EXT.test(f.name)) continue;
    const rel = f.webkitRelativePath || f.name;
    const parts = rel.split("/").filter(Boolean);
    const filename = parts.pop();
    let node = root;
    let pathAcc = "";
    for (const part of parts) {
      pathAcc = pathAcc ? `${pathAcc}/${part}` : part;
      if (!node.children[part]) {
        node.children[part] = { name: part, path: pathAcc, children: {}, files: [] };
      }
      node = node.children[part];
    }
    node.files.push({ file: f, name: filename });
  }
  return root;
}

function collectLeafFolders(node, out = []) {
  const isLeaf = node.files.length > 0;
  if (isLeaf) out.push(node);
  for (const child of Object.values(node.children)) {
    collectLeafFolders(child, out);
  }
  return out;
}

function totalPhotos(node) {
  let n = node.files.length;
  for (const c of Object.values(node.children)) n += totalPhotos(c);
  return n;
}

// ---- Component ------------------------------------------------------------
export default function FolderUpload({ events, onUploaded }) {
  const { user } = useAuth();
  const defaultCur = (user?.default_currency || "gbp").toLowerCase();
  const folderInputRef = useRef();

  const [rootFolder, setRootFolder] = useState("");
  const [tree, setTree] = useState(null);          // full tree for display
  const [leaves, setLeaves] = useState([]);        // flat list of terminal folders
  const [assignments, setAssignments] = useState({}); // { folderPath: { event_id, group_id, price, currency, title_prefix, skip } }
  const [bulkEventId, setBulkEventId] = useState("");
  const [bulkGroupId, setBulkGroupId] = useState("");
  const [bulkPrice, setBulkPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({
    folder: 0, folders: 0,
    done: 0, total: 0,
    batchBytes: 0, batchBytesTotal: 0, // current batch upload bytes (from axios)
    currentFolder: "",
    startedAt: 0,
    errors: [],
  });
  // Throttle ref no longer used on main thread — the worker owns progress ticks —
  // but keep the ref name-cleared so nothing dangling. See folderUploader.worker.js.
  const workerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [resumeInfo, setResumeInfo] = useState(null); // { count, total, failed } if a previous session exists
  const totalFilesInTree = useRef(0);

  // Terminate any running worker when the component unmounts so an in-flight
  // upload doesn't outlive the /admin page navigation. Uploads that were
  // already POSTed to the backend are committed regardless.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount cleanup only; workerRef is a stable ref
  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  // Shared handler used by both the folder picker and the drop zone.
  const ingestFiles = (files) => {
    if (!files || files.length === 0) {
      toast.warning("No image files found in that folder.");
      return;
    }
    const t = buildTree(files);
    const root = files[0].webkitRelativePath?.split("/")[0] || "(folder)";
    const leafNodes = collectLeafFolders(t);
    const initial = {};
    for (const leaf of leafNodes) {
      initial[leaf.path] = {
        event_id: "",
        group_id: "",
        price: "",
        currency: defaultCur,
        title_prefix: leaf.name,
        skip: false,
      };
    }
    // Check for a leftover manifest from a previous crashed upload
    const manifest = loadManifest(root, files.length);
    totalFilesInTree.current = files.length;
    setTree(t);
    setLeaves(leafNodes);
    setRootFolder(root);
    setAssignments(initial);
    const uploadedCount = manifest.uploaded?.length || 0;
    const failedList = manifest.failed || [];
    // Seed the on-screen errors list from the persisted manifest so the
    // "Retry X failed" button is available even after a full page reload.
    setProgress((p) => ({ ...p, errors: failedList }));
    setResumeInfo(
      uploadedCount || failedList.length
        ? { count: uploadedCount, total: files.length, failed: failedList.length }
        : null,
    );
  };

  const onPickFolder = (e) => {
    const files = Array.from(e.target.files || []);
    ingestFiles(files);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!e.dataTransfer) return;
    setScanning(true);
    try {
      const files = await filesFromDrop(e.dataTransfer);
      ingestFiles(files);
    } catch (err) {
      toast.error("Could not read dropped folder.");
    } finally {
      setScanning(false);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const onDragLeave = (e) => {
    // Ignore leaves from child elements
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const reset = () => {
    setTree(null);
    setLeaves([]);
    setAssignments({});
    setRootFolder("");
    setBulkEventId("");
    setBulkGroupId("");
    setBulkPrice("");
    setProgress({ folder: 0, folders: 0, done: 0, total: 0, batchBytes: 0, batchBytesTotal: 0, currentFolder: "", startedAt: 0, errors: [] });
    setResumeInfo(null);
    totalFilesInTree.current = 0;
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const discardManifest = () => {
    clearManifest(rootFolder, totalFilesInTree.current || 0);
    setResumeInfo(null);
    setProgress((p) => ({ ...p, errors: [] }));
    toast.success("Previous progress cleared — next upload starts from zero");
  };

  const updateAssignment = (path, patch) => {
    setAssignments((prev) => ({ ...prev, [path]: { ...prev[path], ...patch } }));
  };

  const bulkApply = () => {
    if (!bulkEventId && !bulkGroupId && !bulkPrice) {
      toast.warning("Set at least one bulk field first.");
      return;
    }
    setAssignments((prev) => {
      const next = { ...prev };
      for (const p of Object.keys(next)) {
        if (next[p].skip) continue;
        next[p] = {
          ...next[p],
          event_id: bulkEventId || next[p].event_id,
          group_id: bulkEventId ? (bulkGroupId || "") : next[p].group_id, // clear group if event changed
          price: bulkPrice || next[p].price,
        };
      }
      return next;
    });
    toast.success("Applied to all folders");
  };

  const groupsFor = (eventId) => {
    const ev = events.find((e) => e.id === eventId);
    return ev?.groups || [];
  };

  const bulkGroups = groupsFor(bulkEventId);

  // Validate before upload
  const validate = () => {
    let ok = true;
    const errors = [];
    for (const leaf of leaves) {
      const a = assignments[leaf.path];
      if (a.skip) continue;
      if (!a.event_id) { errors.push(`${leaf.path}: event missing`); ok = false; }
      if (!a.group_id) { errors.push(`${leaf.path}: class missing`); ok = false; }
      if (a.price === "" || a.price === null || a.price === undefined || Number(a.price) < 0) { errors.push(`${leaf.path}: price required (0 or more)`); ok = false; }
    }
    return { ok, errors };
  };

  const runUpload = ({ retryOnlyPaths = null } = {}) => {
    const { ok, errors } = validate();
    if (!ok) {
      toast.error(errors[0] + (errors.length > 1 ? ` (+${errors.length - 1} more)` : ""));
      return;
    }
    const activeLeaves = leaves.filter((l) => !assignments[l.path].skip);

    // Compute totals for the "nothing to do" toast and initial UI state.
    const manifest = loadManifest(rootFolder, totalFilesInTree.current || 0);
    const alreadyDone = new Set(manifest.uploaded || []);
    // MEDIUM (code review): total must be the *grand total* of the run so the
    // progress bar never renders >100% on resume. During retry it's the size
    // of the retry set; otherwise it's every file in every active leaf.
    const totalPhotos = retryOnlyPaths
      ? activeLeaves.reduce(
          (n, l) => n + l.files.filter(({ file }) => retryOnlyPaths.has(`${l.path}/${file.name}`)).length,
          0,
        )
      : activeLeaves.reduce((n, l) => n + l.files.length, 0);
    // "remaining" is what still needs to hit the network — used only for the
    // "nothing to upload" short-circuit.
    const remainingToUpload = retryOnlyPaths
      ? totalPhotos
      : totalPhotos - alreadyDone.size;
    if (remainingToUpload <= 0) {
      toast.error(retryOnlyPaths ? "No failed files match the current folder." : "Nothing to upload — every folder is set to Skip.");
      return;
    }

    // Build the payload the worker will receive. File objects are structured-
    // cloneable and share the underlying blob store — no bytes copied.
    const payloadLeaves = activeLeaves.map((leaf) => ({
      path: leaf.path,
      files: leaf.files.map(({ file, name }) => ({ file, name })),
      assignment: assignments[leaf.path],
    }));

    setBusy(true);
    const startedAt = Date.now();
    setProgress({
      folder: 0, folders: activeLeaves.length,
      done: retryOnlyPaths ? 0 : alreadyDone.size,
      total: totalPhotos,
      batchBytes: 0, batchBytesTotal: 0,
      currentFolder: "",
      startedAt,
      errors: manifest.failed || [],
    });

    // Spawn a fresh worker per run so a hung upload can't poison the next one.
    workerRef.current?.terminate();
    const worker = new Worker(new URL("../../workers/folderUploader.worker.js", import.meta.url));
    workerRef.current = worker;

    const token = localStorage.getItem("auth_token") || "";
    const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

    worker.onmessage = (evt) => {
      const msg = evt.data;
      switch (msg.type) {
        case "started":
          // Worker recomputed authoritative total — sync in case main thread
          // and worker diverged (they shouldn't, but be defensive).
          setProgress((p) => ({ ...p, done: msg.done, total: msg.total, errors: msg.errors }));
          break;
        case "folder-start":
          setProgress((p) => ({ ...p, folder: msg.folder, currentFolder: msg.currentFolder }));
          break;
        case "batch-start":
          setProgress((p) => ({ ...p, batchBytes: 0, batchBytesTotal: msg.batchBytesTotal }));
          break;
        case "batch-progress":
          setProgress((p) => ({ ...p, batchBytes: msg.batchBytes }));
          break;
        case "batch-done":
          setProgress((p) => ({ ...p, done: msg.done, errors: msg.errors, batchBytes: msg.batchBytes }));
          saveManifest(rootFolder, totalFilesInTree.current || 0, msg.manifest.uploaded, msg.manifest.failed);
          break;
        case "done": {
          setBusy(false);
          workerRef.current = null;
          worker.terminate();
          const failed = msg.errors || [];
          if (msg.fatal) {
            toast.error(`Uploader failed: ${msg.fatal}`);
          } else if (failed.length === 0) {
            clearManifest(rootFolder, totalFilesInTree.current || 0);
            if (retryOnlyPaths) {
              toast.success(`Retry complete — all ${msg.totalUploaded} previously failed photos uploaded`);
              setResumeInfo(null);
            } else {
              toast.success(`Uploaded ${msg.totalUploaded} photos across ${msg.folders} folders`);
              reset();
            }
          } else {
            saveManifest(rootFolder, totalFilesInTree.current || 0, msg.manifest.uploaded, msg.manifest.failed);
            toast.warning(`${msg.totalUploaded} uploaded · ${failed.length} failed — hit Retry to try them again`);
          }
          onUploaded && onUploaded();
          break;
        }
        default:
          break;
      }
    };

    worker.onerror = (e) => {
      console.error("Uploader worker crashed", e);
      toast.error(`Uploader worker crashed: ${e.message || "unknown error"}`);
      setBusy(false);
      workerRef.current = null;
      worker.terminate();
    };

    worker.postMessage({
      type: "start",
      payload: {
        apiBase,
        token,
        activeLeaves: payloadLeaves,
        alreadyDoneList: Array.from(alreadyDone),
        existingFailed: manifest.failed || [],
        retryOnlyPaths: retryOnlyPaths ? Array.from(retryOnlyPaths) : null,
      },
    });
  };

  // Build the retry set from the on-screen errors (which are seeded from the
  // manifest on load, so this survives a page reload). Only include files
  // that still exist in the currently loaded folder tree.
  const retryFailed = () => {
    const failedList = progress.errors || [];
    if (failedList.length === 0) return;
    const wanted = new Set(failedList.map((e) => `${e.folder}/${e.filename}`));
    // Sanity-check: warn if some failed files aren't in the current tree
    // (e.g. the user picked a different folder than before).
    const treePaths = new Set();
    for (const leaf of leaves) {
      for (const { file } of leaf.files) treePaths.add(`${leaf.path}/${file.name}`);
    }
    const missing = [...wanted].filter((p) => !treePaths.has(p));
    if (missing.length === wanted.size) {
      toast.error("None of the failed files are in this folder — pick the same folder as before.");
      return;
    }
    if (missing.length > 0) {
      toast.warning(`${missing.length} failed file(s) missing from this folder — retrying the rest.`);
    }
    runUpload({ retryOnlyPaths: wanted });
  };

  // ---- Render ----------------------------------------------------------------
  if (events.length === 0) {
    return (
      <div className="border border-white/10 p-10 text-center" data-testid="folder-upload-no-events">
        <p className="text-white/60 mb-2 font-serif text-2xl">Create an event first</p>
        <p className="text-white/40 text-sm">Head to the Events tab and add the date + track before uploading photos.</p>
      </div>
    );
  }

  if (!tree) {
    return (
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`border-2 border-dashed p-10 md:p-16 text-center bg-[#0a0a0a] transition-colors ${
          isDragging ? "border-[var(--pm-orange)] bg-[#1a0a00]" : "border-[var(--pm-orange-40)]"
        }`}
        data-testid="folder-upload-empty"
      >
        <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-3">Folder mode</p>
        <h3 className="font-display text-3xl md:text-4xl uppercase mb-4">
          {isDragging ? "Drop it here" : "Upload a whole folder"}
        </h3>
        <p className="text-sm text-white/60 max-w-2xl mx-auto mb-8 font-condensed">
          {scanning
            ? "Reading folder tree…"
            : (<>
                <span className="pm-orange font-semibold">Drag your folder</span> onto this area, or click below to pick one.
                We&apos;ll show every subfolder so you can drag each to its own event + class.
              </>)}
        </p>
        <input
          ref={folderInputRef}
          type="file"
          webkitdirectory="true"
          multiple
          onChange={onPickFolder}
          className="hidden"
          data-testid="folder-upload-input"
        />
        <button
          onClick={() => folderInputRef.current?.click()}
          disabled={scanning}
          className="bg-[var(--pm-orange)] text-black px-8 py-4 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[var(--pm-orange-bright)] disabled:opacity-50"
          data-testid="folder-upload-pick"
        >
          {scanning ? "Scanning…" : "Choose folder"}
        </button>
        <p className="text-xs text-white/40 mt-6 font-condensed">
          Drag &amp; drop works in Chrome, Edge, Safari and modern Firefox. The picker button works everywhere.
        </p>
      </div>
    );
  }

  const totalCount = totalPhotos(tree);
  const skipped = leaves.filter((l) => assignments[l.path]?.skip).length;
  const remaining = leaves.length - skipped;

  return (
    <div
      className="space-y-6 relative"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      data-testid="folder-upload-review"
    >
      {isDragging && !busy && (
        <div className="fixed inset-0 bg-black/70 border-4 border-dashed border-[var(--pm-orange)] z-50 flex items-center justify-center pointer-events-none" data-testid="folder-upload-drop-overlay">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-3">Drop to replace</p>
            <h3 className="font-display text-5xl uppercase">Drop folder</h3>
          </div>
        </div>
      )}
      {/* Big sticky progress panel — visible while uploading */}
      {busy && <UploadProgressPanel progress={progress} />}

      {/* Resume-from-crash banner */}
      {resumeInfo && !busy && (
        <div className="border border-[var(--pm-orange)] bg-[#1a0a00] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="folder-upload-resume-banner">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-1">
              {resumeInfo.failed > 0 ? "Previous upload had failures" : "Resume previous upload"}
            </p>
            <p className="text-sm text-white/80 font-condensed">
              <span className="pm-orange font-semibold">{resumeInfo.count}</span> of {resumeInfo.total} photos uploaded
              {resumeInfo.failed > 0 && (
                <>
                  {" · "}
                  <span className="text-red-400 font-semibold">{resumeInfo.failed}</span> failed
                </>
              )}
              {". "}
              {resumeInfo.failed > 0
                ? "Hit Retry below to re-send just the failed files, or Upload to resume the rest."
                : "When you hit Upload, we'll skip those and only send the missing "}
              {resumeInfo.failed === 0 && (
                <span className="pm-orange font-semibold">{resumeInfo.total - resumeInfo.count}</span>
              )}
              {resumeInfo.failed === 0 && "."}
            </p>
          </div>
          <button
            onClick={discardManifest}
            className="text-xs uppercase tracking-[0.3em] text-white/60 border border-white/20 hover:border-white/60 px-4 py-2"
            data-testid="folder-upload-discard-manifest"
          >
            Start over instead
          </button>
        </div>
      )}

      {/* Header */}
      <div className="border border-white/10 bg-[#0a0a0a] p-5 flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 font-condensed">Selected folder</p>
          <h3 className="font-display text-3xl uppercase mt-1" data-testid="folder-upload-root">{rootFolder}</h3>
          <p className="text-sm text-white/60 mt-1 font-condensed">
            <span className="pm-orange font-semibold">{totalCount}</span> photos across{" "}
            <span className="pm-orange font-semibold">{leaves.length}</span> folders{skipped > 0 && ` · ${skipped} skipped`}
          </p>
        </div>
        <button
          onClick={reset}
          disabled={busy}
          className="border border-white/20 hover:border-white/60 text-xs uppercase tracking-[0.3em] px-4 py-3 disabled:opacity-50"
          data-testid="folder-upload-reset"
        >
          Pick a different folder
        </button>
      </div>

      {/* Bulk-apply strip */}
      <div className="border border-[var(--pm-orange-30)] bg-[#0a0a0a] p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="folder-upload-bulk">
        <div className="md:col-span-4">
          <label className="text-[10px] uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Set event for all</label>
          <select
            value={bulkEventId}
            onChange={(e) => { setBulkEventId(e.target.value); setBulkGroupId(""); }}
            className="input-minimal w-full bg-transparent"
            data-testid="folder-upload-bulk-event"
          >
            <option value="">— pick event —</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.track} · {new Date(ev.date).toLocaleDateString("en-GB")}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-3">
          <label className="text-[10px] uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Class for all</label>
          <select
            value={bulkGroupId}
            onChange={(e) => setBulkGroupId(e.target.value)}
            className="input-minimal w-full bg-transparent"
            disabled={!bulkEventId}
            data-testid="folder-upload-bulk-group"
          >
            <option value="">— pick class —</option>
            {bulkGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Price</label>
          <input
            type="number" min="0" step="0.01"
            value={bulkPrice}
            onChange={(e) => setBulkPrice(e.target.value)}
            className="input-minimal w-full"
            placeholder="e.g. 5.00"
            data-testid="folder-upload-bulk-price"
          />
        </div>
        <div className="md:col-span-3 flex justify-end">
          <button
            onClick={bulkApply}
            disabled={busy}
            className="border border-[var(--pm-orange)] pm-orange hover:bg-[var(--pm-orange)] hover:text-black text-xs uppercase tracking-[0.3em] px-4 py-3 font-semibold disabled:opacity-50"
            data-testid="folder-upload-bulk-apply"
          >
            Apply to all
          </button>
        </div>
      </div>

      {/* Folder rows */}
      <div className="space-y-2" data-testid="folder-upload-list">
        {leaves.map((leaf) => {
          const a = assignments[leaf.path] || {};
          const groups = groupsFor(a.event_id);
          const preview = leaf.files[0];
          const isReady = a.event_id && a.group_id && a.price !== "" && a.price !== undefined && Number(a.price) >= 0;
          return (
            <div
              key={leaf.path}
              className={`border p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center ${
                a.skip ? "border-white/5 bg-black opacity-50" :
                isReady ? "border-green-500/30 bg-[#0a1a0a]" : "border-white/10 bg-[#0a0a0a]"
              }`}
              data-testid={`folder-row-${leaf.path}`}
            >
              {/* Preview */}
              <div className="md:col-span-1 flex justify-center">
                {preview ? (
                  <FilePreview file={preview.file} />
                ) : (
                  <div className="w-14 h-14 bg-white/5" />
                )}
              </div>
              {/* Path + count */}
              <div className="md:col-span-3">
                <p className="text-xs font-mono text-white/60 truncate" title={leaf.path}>{leaf.path}</p>
                <p className="text-xs pm-orange font-semibold uppercase tracking-[0.2em] mt-1 font-condensed">
                  {leaf.files.length} {leaf.files.length === 1 ? "photo" : "photos"}
                </p>
              </div>
              {/* Event */}
              <div className="md:col-span-3">
                <select
                  value={a.event_id || ""}
                  onChange={(e) => updateAssignment(leaf.path, { event_id: e.target.value, group_id: "" })}
                  className="input-minimal w-full bg-transparent text-sm"
                  disabled={a.skip || busy}
                  data-testid={`folder-event-${leaf.path}`}
                >
                  <option value="">— event —</option>
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.track}</option>
                  ))}
                </select>
              </div>
              {/* Group */}
              <div className="md:col-span-2">
                <select
                  value={a.group_id || ""}
                  onChange={(e) => updateAssignment(leaf.path, { group_id: e.target.value })}
                  className="input-minimal w-full bg-transparent text-sm"
                  disabled={!a.event_id || a.skip || busy}
                  data-testid={`folder-group-${leaf.path}`}
                >
                  <option value="">— class —</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              {/* Price */}
              <div className="md:col-span-1">
                <input
                  type="number" min="0" step="0.01"
                  value={a.price ?? ""}
                  onChange={(e) => updateAssignment(leaf.path, { price: e.target.value })}
                  className="input-minimal w-full text-sm"
                  disabled={a.skip || busy}
                  placeholder="£ (0 = free)"
                  data-testid={`folder-price-${leaf.path}`}
                />
              </div>
              {/* Skip */}
              <div className="md:col-span-2 flex justify-end gap-2">
                <button
                  onClick={() => updateAssignment(leaf.path, { skip: !a.skip })}
                  disabled={busy}
                  className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-2 font-semibold disabled:opacity-50 ${
                    a.skip ? "border-[var(--pm-orange)] pm-orange" : "border-white/20 text-white/50 hover:border-white/60"
                  }`}
                  data-testid={`folder-skip-${leaf.path}`}
                >
                  {a.skip ? "Include" : "Skip"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Upload button */}
      <div className="border-t border-white/10 pt-6 flex flex-wrap items-center justify-between gap-4">
        <div className="text-xs font-condensed text-white/60">
          Ready: <span className="pm-orange font-semibold">{remaining}</span> {remaining === 1 ? "folder" : "folders"}
        </div>
        <button
          onClick={() => runUpload()}
          disabled={busy || remaining === 0}
          className="bg-[var(--pm-orange)] text-black px-8 py-4 text-sm uppercase tracking-[0.3em] font-bold hover:bg-[var(--pm-orange-bright)] disabled:opacity-50"
          data-testid="folder-upload-submit"
        >
          {busy ? "Uploading…" : `Upload ${remaining} ${remaining === 1 ? "folder" : "folders"}`}
        </button>
      </div>

      {progress.errors.length > 0 && (
        <div className="border border-red-500/40 bg-[#1a0000] p-4" data-testid="folder-upload-errors">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-xs uppercase tracking-[0.3em] text-red-400 font-semibold">
              {progress.errors.length} {progress.errors.length === 1 ? "error" : "errors"}
            </p>
            <button
              onClick={retryFailed}
              disabled={busy}
              className="bg-[var(--pm-orange)] text-black px-5 py-2 text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--pm-orange-bright)] disabled:opacity-50"
              data-testid="folder-upload-retry-failed"
            >
              {busy ? "Uploading…" : `Retry ${progress.errors.length} failed`}
            </button>
          </div>
          <ul className="text-xs font-mono space-y-1 max-h-48 overflow-y-auto">
            {progress.errors.slice(0, 20).map((err) => (
              <li key={`${err.folder}/${err.filename}`} className="text-white/70 truncate">
                <span className="text-red-400">✗</span> {err.folder}/{err.filename} — <span className="text-red-300">{err.error}</span>
              </li>
            ))}
            {progress.errors.length > 20 && (
              <li className="text-white/40">…and {progress.errors.length - 20} more</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// Big progress panel used while uploading — sticky at the top of the review
// area so photographers can watch it without scrolling. Shows overall + current
// batch progress + ETA computed from a rolling photos-per-second rate.
function UploadProgressPanel({ progress }) {
  const {
    folder, folders, done, total,
    batchBytes, batchBytesTotal, currentFolder, startedAt, errors,
  } = progress;
  const overallPct = total > 0 ? (done / total) * 100 : 0;
  const batchPct = batchBytesTotal > 0 ? Math.min(100, (batchBytes / batchBytesTotal) * 100) : 0;
  const elapsedMs = startedAt ? Date.now() - startedAt : 0;
  const rate = elapsedMs > 3000 && done > 0 ? done / (elapsedMs / 1000) : 0;
  const remaining = Math.max(0, total - done);
  const etaSec = rate > 0 ? Math.round(remaining / rate) : null;
  const etaLabel = etaSec === null ? "…" :
    etaSec < 60 ? `${etaSec}s` :
    etaSec < 3600 ? `${Math.floor(etaSec / 60)}m ${etaSec % 60}s` :
    `${Math.floor(etaSec / 3600)}h ${Math.floor((etaSec % 3600) / 60)}m`;
  const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };
  return (
    <div
      className="sticky top-16 z-30 border-2 border-[var(--pm-orange)] bg-black shadow-[0_8px_40px_rgba(0,0,0,0.7)] p-5 md:p-6"
      data-testid="folder-upload-progress-panel"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] pm-orange font-semibold">Uploading</p>
          <h4 className="font-display text-3xl md:text-4xl uppercase mt-1" data-testid="upload-progress-headline">
            {done}<span className="text-white/40"> / {total}</span> photos
          </h4>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Estimated left</p>
          <p className="font-display text-2xl md:text-3xl uppercase pm-orange" data-testid="upload-progress-eta">{etaLabel}</p>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="mb-2" data-testid="upload-progress-overall">
        <div className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.3em] mb-1">
          <span className="text-white/50">Overall</span>
          <span className="pm-orange font-semibold">{overallPct.toFixed(1)}%</span>
        </div>
        <div className="h-3 bg-white/10 overflow-hidden">
          <div
            className="h-full bg-[var(--pm-orange)] transition-[width] duration-200 ease-out"
            style={{ width: `${overallPct}%` }}
          />
        </div>
      </div>

      {/* Batch progress bar (bytes for current 8-photo batch) */}
      <div className="mt-3 mb-1" data-testid="upload-progress-batch">
        <div className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.3em] mb-1">
          <span className="text-white/50">Current batch</span>
          <span className="text-white/70 font-condensed">
            {fmtBytes(batchBytes)} / {fmtBytes(batchBytesTotal)}
          </span>
        </div>
        <div className="h-2 bg-white/10 overflow-hidden">
          <div
            className="h-full bg-white/60 transition-[width] duration-150 ease-out"
            style={{ width: `${batchPct}%` }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-3 mt-4 text-xs font-condensed">
        <div className="text-white/60 truncate min-w-0">
          <span className="text-white/40 uppercase tracking-[0.2em]">Folder {folder}/{folders} · </span>
          <span className="pm-orange font-semibold" data-testid="upload-progress-current-folder">{currentFolder || "…"}</span>
        </div>
        <div className="flex items-center gap-4">
          {rate > 0 && (
            <span className="text-white/50" data-testid="upload-progress-rate">
              {rate.toFixed(1)} <span className="text-white/30">photos/sec</span>
            </span>
          )}
          {errors.length > 0 && (
            <span className="text-red-400" data-testid="upload-progress-errors-tally">
              {errors.length} failed
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Preview thumbnail — decodes + shrinks the File on a background canvas
// BEFORE handing it to <img>. Otherwise Chrome decodes the full-res JPG
// (~350MB bitmap for a 24MP photo) just to paint a 56px square, which
// OOMs the tab when there are 30+ folder rows all mounting at once.
// Native createImageBitmap with resize hints does the decode off-thread.
function FilePreview({ file }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      try {
        // 112 = 56 CSS px × 2x for retina. resizeQuality low is plenty at this size.
        const bmp = await createImageBitmap(file, {
          resizeWidth: 112,
          resizeHeight: 112,
          resizeQuality: "low",
        });
        if (cancelled) { bmp.close?.(); return; }
        const canvas = document.createElement("canvas");
        canvas.width = 112;
        canvas.height = 112;
        canvas.getContext("2d").drawImage(bmp, 0, 0);
        bmp.close?.();
        // Export as ~5-10KB JPEG blob — releases the source File decode buffer
        const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.6));
        if (cancelled || !blob) { if (!cancelled) setFailed(true); return; }
        currentUrl = URL.createObjectURL(blob);
        setUrl(currentUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [file]);

  if (failed) return <div className="w-14 h-14 bg-white/5" />;
  return url ? (
    <img
      src={url}
      alt=""
      className="w-14 h-14 object-cover"
      loading="lazy"
      decoding="async"
    />
  ) : (
    <div className="w-14 h-14 bg-white/5 animate-pulse" />
  );
}
